// ReviewAndSign.tsx
import { datadogRum } from "@datadog/browser-rum";
import { ArrowDownIcon } from "@heroicons/react/24/solid";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { getPayorConfig, hasPayorSubmissionIntegration } from "@utils/payorConfig";
import { getLatestPayorReview } from "@utils/reportStatus";
import { formatDate, scrollToElementById, useNavigateById } from "@utils/utils";
import clsx from "clsx";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";

import { Dialog, Headline, Paragraph, Select, SelectItem, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiPayorReportReviewStatusChoices,
  ApiPayorReportSubmissionStatusChoices,
  ApiReportReportStatusChoices,
  ApiReportReportTypeChoices,
  ApiReportTemplateExportFrameworkChoices,
  ApiReportTemplateFileUploadConfigChoices,
  ExportTypeEnum,
  ReportType,
} from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { LayoutAside, LayoutBody, LayoutMain } from "@components/Layout";
import { PayorFeedbackBanner } from "@components/PayorFeedbackBanner/PayorFeedbackBanner";
import { PreThinkingScreen } from "@components/QaReview/components/PreThinkingScreen";
import { ReviewBar } from "@components/QaReview/components/ReviewBar";
import { ReviewColumn } from "@components/QaReview/components/ReviewColumn";
import { BlankState, SkeletonState } from "@components/QaReview/components/ReviewStates";
import { SuggestionDisclaimer } from "@components/QaReview/components/SuggestionDisclaimer";
import { useFirstTimeDisclaimer } from "@components/QaReview/hooks/useFirstTimeDisclaimer";
import { useIsQaScannerEnabled } from "@components/QaReview/hooks/useIsQaScannerEnabled";
import { useIsReviewPanelVisible } from "@components/QaReview/hooks/useIsReviewPanelVisible";
import { useReviewData } from "@components/QaReview/hooks/useReviewData";
import { useReviewGate } from "@components/QaReview/hooks/useReviewGate";
import { useReviewSession } from "@components/QaReview/hooks/useReviewSession";
import { PayorSubmissionDialog } from "@components/ReportCompletionAndExport/PayorSubmissionDialog";
import { PayorSubmittingDialog } from "@components/ReportCompletionAndExport/PayorSubmittingDialog";
import { ReportCompletionDialog } from "@components/ReportCompletionAndExport/ReportCompletionDialog";
import { EXPORT_OPTIONS, ReportExportDialog } from "@components/ReportCompletionAndExport/ReportExportDialog";
import { ReportSection } from "@components/ReportField/ReportSection";
import { useReportForm } from "@components/ReportField/useReportForm";
import { ReportHeaderSection } from "@components/ReportHeaderSection/ReportHeaderSection.tsx";
import { notifySuccess, notifyWarn } from "@components/notifications/notifications";
import { useInFlightOnce } from "@hooks/useInFlightOnce";
import { usePayorReportSubmission } from "@hooks/usePayorReportSubmission";
import {
  computeTotalDaysToComplete,
  countInsuranceFlagsRemaining,
  useReportAnalytics,
} from "@hooks/useReportAnalytics";
import { useReportExport } from "@hooks/useReportExport";
import { useScrollSync } from "@hooks/useScrollSync";
import { useSignatureHandler } from "@hooks/useSignatureHandler";
import { useTemplateExport } from "@hooks/useTemplateExport";
import { FooterButtonRow } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/FooterButtonRow";
import { ReportSummaryPart2 } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/ReportSummaries/ReportSummaryPart2/ReportSummaryPart2";
import { StickyNav } from "@pages/AssessmentReportV2Details/AssessmentReportSubPages/ReportSummaries/StickyNav/StickyNav";
import { useGetFilteredPageSectionsByLocation } from "@pages/AssessmentReportV2Details/Components/helper";
import { FocusedFieldManagerProvider } from "@pages/ClinicalReportDetails/Providers/FocusedFieldProvider";
import { findErrorSection, isGeneratingSection } from "@pages/EvaluationDetails/Components/helper";
import { useAdminData } from "@providers/AdminDataProvider";
import { useReport } from "@providers/ReportProvider";
import { useSidebarView } from "@providers/SidebarViewProvider/useSidebarView";

const ReviewHeader: React.FC = () => {
  return (
    <div className="flex w-full flex-col">
      <div className="flex items-center mb-4 gap-5 ">
        <div className="h-10 w-10 bg-icon-primary rounded-md p-1 flex items-center justify-center">
          <ArrowDownIcon className="h-6 w-6 text-white " />
        </div>
        <div>
          <Headline size="medium">Review Assessment Report</Headline>

          <Paragraph displayType="normal" colorType="secondary">
            Please review the information below to verify it’s accuracy before approving the final document.
          </Paragraph>
        </div>
      </div>
    </div>
  );
};

const topPageElementId = "reviewAndSign";

interface LoadingViewInput {
  shouldShowPreThinkingScreen: boolean;
  showDisclaimer: boolean;
  confirmDisclaimer: () => void;
  isReportQaScannerEnabled: boolean;
  isReportCompleted: boolean;
  hasReport: boolean;
  loadingState: "blank" | "skeleton" | "loaded";
  isReviewPanelVisible: boolean;
  suggestionsScrollRef: React.RefObject<HTMLDivElement | null>;
}

const renderLoadingView = (input: LoadingViewInput): React.ReactElement | null => {
  // The disclaimer opens on load, over the PreThinkingScreen or a blank page, and stays until Confirm.
  // One wrapper for both keeps the dialog mounted when the PreThinkingScreen finishes.
  if (input.shouldShowPreThinkingScreen || input.showDisclaimer) {
    return (
      <div className={clsx("flex w-full flex-col", !input.shouldShowPreThinkingScreen && "bg-limestone-50")}>
        {input.shouldShowPreThinkingScreen ? <PreThinkingScreen /> : <BlankState />}
        <SuggestionDisclaimer isOpen={input.showDisclaimer} onConfirm={input.confirmDisclaimer} />
      </div>
    );
  }
  const progressiveReady = input.isReportQaScannerEnabled && input.hasReport && !input.isReportCompleted;
  // Blank, then skeleton, 300ms each
  if (input.loadingState === "blank" && progressiveReady) return <BlankState />;
  if (input.loadingState === "skeleton" && progressiveReady) {
    return (
      <SkeletonState
        isReviewPanelVisible={input.isReviewPanelVisible}
        suggestionsScrollRef={input.suggestionsScrollRef}
      />
    );
  }
  return null;
};

export const ReviewAndSign: React.FC = () => {
  const { assessmentId } = useParams();
  const { report, reportQuery } = useReport();
  // Full reportContext so report_completed carries the same template fields
  // the wizard-level report_opened + wizard_step_* events already do.
  const { captureReportCompleted } = useReportAnalytics({
    report_id: assessmentId ?? "",
    report_type: "initial_assessment",
    template_id: report?.reportTemplate?.id,
    template_name: report?.reportTemplate?.name,
    template_payor_type: report?.reportTemplate?.payorType ?? undefined,
    template_export_framework: report?.reportTemplate?.exportFramework ?? undefined,
  });
  const [loadingState, setLoadingState] = useState<"blank" | "skeleton" | "loaded">("blank");
  const [preThinkingScreenJustFinished, setPreThinkingScreenJustFinished] = useState<boolean>(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [selectedExportType, setSelectedExportType] = useState<SelectItem>(EXPORT_OPTIONS[0]);
  const [isCompletionFormatDialogOpen, setIsCompletionFormatDialogOpen] = useState<boolean>(false);
  const [isPayorSubmittingDialogOpen, setIsPayorSubmittingDialogOpen] = useState<boolean>(false);
  const [isAwaitingSubmissionOutcome, setIsAwaitingSubmissionOutcome] = useState<boolean>(false);
  const [payorSubmissionFailed, setPayorSubmissionFailed] = useState<boolean>(false);
  const [payorSubmissionErrorMessage, setPayorSubmissionErrorMessage] = useState<string>("");
  const [selectedCompletionExportType, setSelectedCompletionExportType] = useState<SelectItem>({
    id: ExportTypeEnum.Docx,
    primary: "Microsoft Word (.docx)",
  });

  const filteredSections = useGetFilteredPageSectionsByLocation("report-part-1");
  const filteredSectionsPart2 = useGetFilteredPageSectionsByLocation("report-part-2");
  const [showRequiredErrors, setShowRequiredErrors] = useState<boolean>(false);
  const allFilteredSections = useMemo(
    () => [...filteredSections, ...filteredSectionsPart2],
    [filteredSections, filteredSectionsPart2],
  );
  const methods = useReportForm(allFilteredSections);
  const flags = useFlags<FeatureFlags>();
  const isReportQaScannerEnabled = useIsQaScannerEnabled();
  const isReviewPanelVisible = useIsReviewPanelVisible();
  const { view } = useSidebarView();
  const { state: session } = useReviewSession();
  const { loadedItems, generationStatus: suggestionGenerationStatus } = useReviewData();
  const { isBlocked: isReviewGateBlocked, tooltipReason: reviewGateReason } = useReviewGate();
  // Treat Generating as Completed (matches ClinicalReportDetailsProvider) so
  // the View Report path lands on the Completed view immediately, even while
  // the export workflow is still polling to flip Generating → Completed.
  const reportIsCompleted =
    report?.reportStatus === ApiReportReportStatusChoices.Completed ||
    report?.reportStatus === ApiReportReportStatusChoices.Generating;
  const { isCatalightUser } = useAdminData();
  const isCatalightIa =
    report?.reportTemplate?.fileUploadConfig === ApiReportTemplateFileUploadConfigChoices.Catalight &&
    report?.reportType === ApiReportReportTypeChoices.InitialAssessment;

  const { showDisclaimer, confirmDisclaimer, timeoutReached } = useFirstTimeDisclaimer({
    isReportCompleted: reportIsCompleted,
  });

  const { isLoading } = reportQuery;
  const isSectionGenerating = isGeneratingSection(useGetFilteredPageSectionsByLocation("review-and-sign"));
  const clientName = `${report?.reportClientDetails?.clientFirstName || "N/A"} ${report?.reportClientDetails?.clientLastName || ""}`;
  const clientDateOfBirth = report?.reportClientDetails?.birthDate
    ? formatDate(report.reportClientDetails.birthDate)
    : undefined;

  // PROA-2622: a resolved sign promise says nothing about the outcome -- the
  // polling loop resolves the same way on Completed, Failure and timeout.
  // `onReportCompleted` is the one signal that fires on Completed alone. A ref
  // because the payor branch reads it straight after an await, before a
  // re-render.
  const signReachedCompletedRef = useRef(false);

  const { isGenerating, setIsGenerating, handleSignReport, isCompletionDialogOpen, setIsCompletionDialogOpen } =
    useSignatureHandler({
      report: report,
      isCompleted: reportIsCompleted,
      refetchData: reportQuery?.refetch,
      onReportCompleted: () => {
        signReachedCompletedRef.current = true;
        captureReportCompleted({
          total_days_to_complete: computeTotalDaysToComplete(report?.createdDate),
          insurance_flags_remaining: countInsuranceFlagsRemaining(loadedItems),
        });
      },
    });

  const { handleExport, isExporting } = useReportExport({
    report: report,
    exportType: selectedExportType.id as ExportTypeEnum,
    clientName,
    onExportComplete: () => {
      setIsExportModalOpen(false);
      if (isCompletionDialogOpen) {
        setIsCompletionDialogOpen(false);
      }
    },
  });

  const refetchReportForPdf = useCallback(async (): Promise<ReportType | undefined> => {
    const result = await reportQuery.refetch();
    return result.data?.getReports?.edges?.[0]?.node as ReportType | undefined;
  }, [reportQuery]);

  const {
    handleExport: handleTemplateExport,
    isExporting: isTemplateExporting,
    exportError: templateExportError,
    clearExportError: clearTemplateExportError,
  } = useTemplateExport({
    reportData: report,
    exportType: isCatalightIa ? (selectedCompletionExportType.id as ExportTypeEnum) : ExportTypeEnum.Docx,
    refetchReport: isCatalightIa ? refetchReportForPdf : undefined,
    onExportComplete: undefined,
  });

  const handleFormatDialogExport = async () => {
    const success = await handleTemplateExport();
    if (success) {
      setIsCompletionFormatDialogOpen(false);
    }
  };

  const {
    isSubmittingToPayorInProgress,
    startPayorSubmissionWorkflow,
    refetchPayorReportSubmissionStatus,
    payorReportSubmissionStatus,
  } = usePayorReportSubmission({
    reportId: report?.id ?? null,
    isReportCompleted: reportIsCompleted,
    payorType: report?.reportTemplate?.payorType,
  });

  const payorType = report?.reportTemplate?.payorType;
  const payorCopy = getPayorConfig(payorType);

  // Catalight (and any future payor-integrated IA) feedback banner — shown
  // when the payor has returned a Needs-Revision review on a completed
  // Initial Assessment. Gated on `reportIsCompleted` so the banner is part
  // of the read-only viewing surface only; clicking Edit flips the report
  // back to Draft and the banner hides for the Step 7 editing flow. Stays
  // until the clinician resubmits, at which point the latest review is no
  // longer NeedsRevision and the gate flips off naturally.
  const latestPayorReview = getLatestPayorReview(report);
  const isInitialAssessment = report?.reportType === ApiReportReportTypeChoices.InitialAssessment;
  const showPayorFeedbackBanner =
    reportIsCompleted &&
    isInitialAssessment &&
    hasPayorSubmissionIntegration(payorType) &&
    latestPayorReview?.status === ApiPayorReportReviewStatusChoices.NeedsRevision;
  const clientInitials =
    `${report?.reportClientDetails?.clientFirstName?.[0] ?? ""}${
      report?.reportClientDetails?.clientLastName?.[0] ?? ""
    }`.toUpperCase() || "Client";
  const isPayorIntegratedTemplate = hasPayorSubmissionIntegration(payorType);
  const showPayorSubmitDialog = isPayorIntegratedTemplate && flags["report-submit-to-payor"];

  const {
    generateAndUploadPdf,
    isExporting: isGeneratingPdfForSubmit,
    isExportDataReady,
  } = useTemplateExport({
    reportData: report,
    exportType: ExportTypeEnum.Pdf,
    refetchReport: refetchReportForPdf,
  });

  const runSubmitToPayor = async () => {
    setPayorSubmissionFailed(false);
    setPayorSubmissionErrorMessage("");
    setIsCompletionDialogOpen(false);
    setIsPayorSubmittingDialogOpen(true);

    let workflowStarted = false;
    try {
      await signReportIfNeeded();
      // Under unified export, signing already triggered the server-side
      // TS-worker generation and the canonical PDF is in S3. Skip the frontend
      // PDF upload so we don't clobber that file — but only when the server
      // PDF actually exists. Falls back to frontend generation for reports
      // signed before unified export was enabled, an in-flight TS-worker (status
      // Generating, URL not yet populated), or a failed server-side export.
      const isDocxTemplater =
        report?.reportTemplate?.exportFramework === ApiReportTemplateExportFrameworkChoices.Docxtemplater;
      const isUnifiedExport = report?.reportTemplate?.unifiedExport ?? false;
      const hasServerPdf = !!report?.reportPdfFileSignedUrl;
      if (!(isDocxTemplater && isUnifiedExport && hasServerPdf)) {
        await generateAndUploadPdf();
      }
      const data = await startPayorSubmissionWorkflow({ reportId: report?.id ?? "" });
      if (data?.startPayorSubmissionWorkflow?.status === false) {
        setPayorSubmissionErrorMessage(data?.startPayorSubmissionWorkflow?.message ?? "");
      } else if (data?.startPayorSubmissionWorkflow?.status) {
        await Promise.all([refetchPayorReportSubmissionStatus(), reportQuery?.refetch()]);
        workflowStarted = true;
      }
    } catch (error: unknown) {
      datadogRum.addError(error, { context: payorCopy?.errorContexts.submissionFailed });
    }

    if (!workflowStarted) {
      setIsPayorSubmittingDialogOpen(false);
      setPayorSubmissionFailed(true);
      setIsCompletionDialogOpen(true);
      return;
    }

    // Workflow accepted on the backend. The submitting dialog stays open while
    // the workflow runs; the useEffect below resolves the loader once the
    // polling reaches a terminal state.
    setIsAwaitingSubmissionOutcome(true);
  };

  // PROA-2622: the same double-click defect this ticket fixes for exports, on a
  // costlier path -- `generateAndUploadPdf` runs the same `buildAndUploadDocx`
  // that flips the report COMPLETED -> GENERATING, and a second click also
  // starts a second payor submission workflow. The Submit button's `isSubmitting`
  // is derived state, so it only disables a render too late.
  const handleSubmitToPayor = useInFlightOnce(runSubmitToPayor);

  // Resolves the submitting-loader once the workflow polling lands on a
  // terminal state. Keeps the loader open for the full payor round-trip
  // rather than the much faster startPayorSubmissionWorkflow mutation.
  useEffect(() => {
    if (!isAwaitingSubmissionOutcome) return;
    const status = payorReportSubmissionStatus;
    if (status === ApiPayorReportSubmissionStatusChoices.Failed) {
      setIsAwaitingSubmissionOutcome(false);
      setIsPayorSubmittingDialogOpen(false);
      setPayorSubmissionFailed(true);
      setIsCompletionDialogOpen(true);
      return;
    }
    if (
      status === ApiPayorReportSubmissionStatusChoices.Completed ||
      status === ApiPayorReportSubmissionStatusChoices.Approved ||
      status === ApiPayorReportSubmissionStatusChoices.NeedsRevision
    ) {
      setIsAwaitingSubmissionOutcome(false);
      setIsPayorSubmittingDialogOpen(false);
      if (status === ApiPayorReportSubmissionStatusChoices.Completed && payorCopy) {
        notifySuccess(payorCopy.notifications.submitted);
      }
      void reportQuery?.refetch();
    }
  }, [isAwaitingSubmissionOutcome, payorReportSubmissionStatus, reportQuery, setIsCompletionDialogOpen, payorCopy]);

  // Left scrollable report container (source of truth)
  const sectionFieldRef = useRef<HTMLDivElement | null>(null);

  // Right scrollable suggestions wrapper
  const suggestionsScrollRef = useRef<HTMLDivElement | null>(null);
  const reviewColumnRef = useRef<HTMLDivElement | null>(null);

  // Shared sign step used by Confirm and View Report. No-ops when the report
  // is already Completed *or* Generating — must mirror reportIsCompleted so
  // we don't fire a redundant sign mutation while the export workflow is
  // still polling its way back to Completed. The html2canvas DOCX generation
  // step was removed in main (#2886) — the sign mutation alone is enough to
  // flip reportStatus.
  const signReportIfNeeded = async () => {
    if (reportIsCompleted) return;
    setIsGenerating(true);
    await handleSignReport(null);
  };

  const scrollToDivById = useNavigateById();
  //should not show catalight (origin) themselves the report header settings, only orgs that inherit
  const isHeaderConfigDisplayed =
    (report?.reportTemplate?.organizationLogoAllowed && !isCatalightUser) || report?.reportTemplate?.isBaseTemplate;

  // Two-way scroll sync between left report and right suggestions panel
  useScrollSync({
    leftRef: sectionFieldRef,
    rightRef: suggestionsScrollRef,
    activeFieldId: session.active?.fieldId ?? null,
  });

  const shouldShowPreThinkingScreen =
    isReportQaScannerEnabled && report && !reportIsCompleted && suggestionGenerationStatus === null && !timeoutReached;

  // Progressive loading states: blank (300ms) -> skeleton (300ms) -> loaded
  // Only starts AFTER PreThinkingScreen finishes
  useEffect(() => {
    // Don't start if PreThinkingScreen is still showing
    if (shouldShowPreThinkingScreen) {
      return;
    }

    // Only start progressive loading if PreThinkingScreen just finished
    if (!preThinkingScreenJustFinished) {
      // If we haven't shown PreThinkingScreen, go straight to loaded
      setLoadingState("loaded");
      return;
    }

    // Start progressive loading sequence after PreThinkingScreen
    setLoadingState("blank");

    // After 300ms, show skeletons
    const skeletonTimeout = setTimeout(() => {
      setLoadingState("skeleton");
    }, 300);

    // After 600ms total (300ms blank + 300ms skeleton), show loaded content
    const loadedTimeout = setTimeout(() => {
      setLoadingState("loaded");
      setPreThinkingScreenJustFinished(false); // Reset flag after loading completes
    }, 600);

    return () => {
      clearTimeout(skeletonTimeout);
      clearTimeout(loadedTimeout);
    };
  }, [shouldShowPreThinkingScreen, preThinkingScreenJustFinished]);

  useEffect(() => {
    if (timeoutReached) {
      setPreThinkingScreenJustFinished(true);
      setLoadingState("blank");
    }
  }, [timeoutReached]);

  const loadingView = renderLoadingView({
    shouldShowPreThinkingScreen: !!shouldShowPreThinkingScreen,
    showDisclaimer,
    confirmDisclaimer,
    isReportQaScannerEnabled,
    isReportCompleted: reportIsCompleted,
    hasReport: !!report,
    loadingState,
    isReviewPanelVisible,
    suggestionsScrollRef,
  });
  if (loadingView) return loadingView;

  // Show loaded content
  return (
    <FormProvider {...methods}>
      <FocusedFieldManagerProvider showRequiredErrors={showRequiredErrors}>
        <LayoutBody id="AssessmentReport">
          <LayoutMain ref={sectionFieldRef} id={topPageElementId} className="py-4">
            {showPayorFeedbackBanner && payorCopy && (
              <PayorFeedbackBanner
                payorDisplayName={payorCopy.displayName}
                feedbackFileSignedUrl={latestPayorReview?.feedbackFileSignedUrl ?? null}
                clientInitials={clientInitials}
              />
            )}
            {!reportIsCompleted && <ReviewHeader />}

            {isHeaderConfigDisplayed && (
              <ReportHeaderSection
                report={report}
                refetchData={reportQuery?.refetch}
                reportIsCompleted={reportIsCompleted}
              />
            )}

            {filteredSections.map((section) => (
              <ReportSection key={section.id} section={section} disabled={reportIsCompleted} />
            ))}

            <ReportSummaryPart2 isReviewPage={true} key={assessmentId} />
          </LayoutMain>

          {isReviewPanelVisible && !reportIsCompleted && (
            <div className="flex-1 animate-panel-in motion-reduce:animate-none">
              <ReviewColumn
                scrollRef={suggestionsScrollRef}
                containerRef={reviewColumnRef}
                containerId="AssessmentReport"
                panelClassName="w-full col-span-2"
              />
            </div>
          )}

          {view === "toc" && (
            <LayoutAside className="animate-panel-in motion-reduce:animate-none">
              <StickyNav
                sections={filteredSections}
                maxHeight={reportIsCompleted ? "max-h-[calc(100vh-230px)]" : "max-h-[calc(100vh-400px)]"}
              />
            </LayoutAside>
          )}
        </LayoutBody>
        {/* Outside the column, so it can drop away after the column has gone. */}
        <ReviewBar open={isReviewPanelVisible && !reportIsCompleted} anchorRef={reviewColumnRef} />
        {!reportIsCompleted && (
          <FooterButtonRow
            dataTestID="complete-report-button"
            primaryButtonTitle="Complete Report"
            primaryButtonLoading={isGenerating || isLoading}
            primaryButtonDisabled={isSectionGenerating || isReviewGateBlocked}
            primaryButtonDisabledTooltipReason={reviewGateReason}
            primaryButtonAction={methods.handleSubmit(
              async () => {
                setShowRequiredErrors(false);
                const errorSection = findErrorSection(filteredSections);
                const scrollInSuggestionsView = isReviewPanelVisible && !reportIsCompleted;

                if (errorSection) {
                  if (scrollInSuggestionsView) {
                    scrollToElementById(errorSection.id);
                  } else {
                    scrollToDivById(errorSection.id);
                  }
                } else {
                  if (scrollInSuggestionsView) {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else {
                    scrollToDivById(topPageElementId);
                  }

                  // For payor-integrated templates, sign first so that the
                  // reportExportData query (gated on Completed status) fetches
                  // before the dialog opens. This prevents the "first click
                  // fails" bug where generateAndUploadPdf ran before export
                  // data was available.
                  if (showPayorSubmitDialog) {
                    setIsGenerating(true);
                    // PROA-2622: the dialog used to open on the sign promise
                    // resolving, which happens identically on Completed, Failure
                    // and timeout -- so a "Report generation timed out." toast was
                    // followed straight by the submit dialog, over a report still
                    // sitting in Generating.
                    //
                    // Every branch of the sign handler awaits its polling loop, so
                    // `onReportCompleted` has already fired by the time this
                    // resumes, whatever the template's export framework. A report
                    // that was already final skips signing altogether and fires
                    // nothing, so that case is carried separately.
                    const signingSkipped = reportIsCompleted;
                    signReachedCompletedRef.current = false;
                    try {
                      await signReportIfNeeded();
                      if (signingSkipped || signReachedCompletedRef.current) {
                        setIsCompletionDialogOpen(true);
                      }
                    } catch {
                      // Toast already shown by handleSignReport's onError
                    } finally {
                      setIsGenerating(false);
                    }
                    return;
                  }
                  setTimeout(() => {
                    setIsCompletionDialogOpen(true);
                  }, 2000);
                  setIsGenerating(true);
                  await handleSignReport(null);
                }
              },
              (errors) => {
                setShowRequiredErrors(true);
                Object.values(errors).forEach((error) => error?.message && notifyWarn(String(error.message)));
                const firstErrorId = Object.keys(errors)[0];
                if (firstErrorId) scrollToElementById(firstErrorId, "center");
              },
            )}
          />
        )}

        {showPayorSubmitDialog ? (
          <>
            <PayorSubmissionDialog
              open={isCompletionDialogOpen}
              payorType={payorType}
              reportId={report?.id ?? ""}
              clientName={clientName}
              dateOfBirth={clientDateOfBirth}
              onSubmitToPayor={handleSubmitToPayor}
              onViewReport={async () => {
                // Report is already signed before the dialog opens.
                // Just close the dialog and let the completed view show.
                setPayorSubmissionFailed(false);
                setPayorSubmissionErrorMessage("");
                setIsCompletionDialogOpen(false);
              }}
              onClose={() => {
                setPayorSubmissionFailed(false);
                setPayorSubmissionErrorMessage("");
                setIsCompletionDialogOpen(false);
              }}
              isSubmitting={isSubmittingToPayorInProgress || isGeneratingPdfForSubmit}
              isSubmitDisabled={!isExportDataReady}
              submissionError={payorSubmissionFailed}
              submissionErrorMessage={payorSubmissionErrorMessage}
              isResubmission={payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.NeedsRevision}
            />
            <PayorSubmittingDialog open={isPayorSubmittingDialogOpen} payorType={payorType} />
          </>
        ) : (
          <ReportCompletionDialog
            open={isCompletionDialogOpen}
            clientName={clientName}
            clientId={report?.learner?.id}
            onExportReport={() => {
              const exportFramework = report?.reportTemplate?.exportFramework;
              const isDocxTemplater = exportFramework === ApiReportTemplateExportFrameworkChoices.Docxtemplater;
              const isHandlebars = exportFramework === ApiReportTemplateExportFrameworkChoices.Handlebars;
              const isUnifiedExport = report?.reportTemplate?.unifiedExport ?? false;
              // The template's `unifiedExport` field routes docxtemplater orgs
              // through the handlebars S3-download path.
              const useDocxTemplaterPath = isDocxTemplater && !isUnifiedExport;
              const useHandlebarsPath = isHandlebars || (isDocxTemplater && isUnifiedExport);

              if (useDocxTemplaterPath) {
                if (isCatalightIa) {
                  setIsCompletionDialogOpen(false);
                  setIsCompletionFormatDialogOpen(true);
                } else {
                  handleTemplateExport();
                }
              } else if (useHandlebarsPath) {
                setIsCompletionDialogOpen(false);
                setIsExportModalOpen(true);
              } else {
                handleExport();
              }
            }}
            onViewCompletedReport={() => {
              setIsCompletionDialogOpen(false);
            }}
            isGenerating={isGenerating}
            isExporting={isExporting || isTemplateExporting}
          />
        )}
        <ReportExportDialog
          open={isExportModalOpen}
          setOpen={() => setIsExportModalOpen(false)}
          onExport={async () => {
            await handleExport();
          }}
          selectedExportType={selectedExportType}
          setSelectedExportType={setSelectedExportType}
          isExporting={isExporting}
          report={report}
          onRetrySuccess={async () => {
            await reportQuery.refetch();
          }}
          isReportLoading={reportQuery.isFetching}
        />
        <Dialog
          title="Export Report"
          open={isCompletionFormatDialogOpen}
          showCloseButton={!isTemplateExporting}
          setOpen={(open) => {
            if (!isTemplateExporting) {
              setIsCompletionFormatDialogOpen(open);
              if (!open) clearTemplateExportError();
            }
          }}
          primaryButton={{
            text: (
              <div className="flex gap-1 w-max items-center">
                {isTemplateExporting && <Loader className="w-4 h-4" />}
                {isTemplateExporting
                  ? selectedCompletionExportType.id === ExportTypeEnum.Pdf
                    ? "Converting to PDF..."
                    : "Exporting..."
                  : "Export"}
              </div>
            ),
            onClick: handleFormatDialogExport,
            disabled: isTemplateExporting,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => {
              setIsCompletionFormatDialogOpen(false);
              clearTemplateExportError();
            },
            disabled: isTemplateExporting,
          }}
        >
          <div className="flex flex-col gap-4 py-2">
            <div className="relative z-10">
              <Select
                title="Export format"
                items={[
                  { id: ExportTypeEnum.Docx, primary: "Microsoft Word (.docx)" },
                  { id: ExportTypeEnum.Pdf, primary: "PDF Document (.pdf)" },
                ]}
                placeholderText="Select format"
                selected={selectedCompletionExportType}
                setSelected={setSelectedCompletionExportType}
                formNoValidate
              />
            </div>
            {templateExportError && <Small className="text-red-500">{templateExportError}</Small>}
          </div>
        </Dialog>
      </FocusedFieldManagerProvider>
    </FormProvider>
  );
};
